import React, {Component} from 'react';
import {action, observable, computed} from 'mobx';
import {inject, observer} from 'mobx-react';
import {Button, FormInput, FormSelect} from 'shards-react';
import FeatherIcon from 'feather-icons-react';
import {toast} from 'react-toastify';
import moment from 'moment';
import PageTitle from '../../../components/common/PageTitle';
import {getMyImages, removeMyImage as removeLocal, renameMyImage as renameLocal} from './myImagesStorage';
import './style.scss';

const SOURCE_LABEL = {
  bgRemover: {label: '배경제거', color: '#7C3AED', bg: '#EDE9FE'},
  upload:    {label: '직접업로드', color: '#0891B2', bg: '#CFFAFE'},
  banner:    {label: '배너', color: '#DB2777', bg: '#FCE7F3'},
  imported:  {label: '가져옴', color: '#6B7280', bg: '#F3F4F6'}
};

const blobToBase64 = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const result = reader.result || '';
    const idx = String(result).indexOf(',');
    resolve(idx >= 0 ? String(result).slice(idx + 1) : String(result));
  };
  reader.onerror = (e) => reject(e);
  reader.readAsDataURL(blob);
});

@inject(({teamUser}) => ({me: teamUser.me}))
@inject(({common}) => ({
  uploadImages: common.uploadImages,
  cafe24UploadImages: common.cafe24UploadImages,
  base64UploadImages: common.base64UploadImages,
  getMyImageList: common.getMyImageList,
  addMyImage: common.addMyImage,
  updateMyImage: common.updateMyImage,
  removeMyImage: common.removeMyImage,
  removeMyImages: common.removeMyImages
}))
@observer
class MyImages extends Component {

  @observable store = {};
  @observable images = [];
  @observable totalCount = 0;
  @observable page = 1;
  @observable totalPages = 1;
  @observable limit = 60;
  @observable keyword = '';
  @observable sourceFilter = '';
  @observable sortKey = 'date_desc'; // date_desc, date_asc, name_asc, size_desc
  @observable isLoading = false;
  @observable isUploading = false;

  @observable editingId = null;
  @observable editingName = '';

  @observable selectedIds = [];
  @observable selectMode = false;

  @observable lightboxImg = null;

  fileInputRef = React.createRef();
  searchTimer = null;

  componentDidMount() {
    if (this.props.me) {
      this.store = this.props.me.store ? this.props.me.store : {};
    }
    this.reload();
  }

  @computed get hasStore() {
    return !!(this.store && this.store._id);
  }

  @action reload = async () => {
    this.isLoading = true;
    let backendList = [];
    let usedBackend = false;
    if (this.hasStore) {
      try {
        let res = await this.props.getMyImageList({
          store_id: this.store._id,
          shopNo: this.store.shopNo || 1,
          page: this.page,
          limit: this.limit,
          keyword: this.keyword || '',
          source: this.sourceFilter || ''
        });
        if (res && res.data && Array.isArray(res.data.docs)) {
          backendList = res.data.docs.map(d => ({
            id: d._id,
            name: d.name || '이미지',
            url: d.url,
            size: d.size || 0,
            width: d.width || 0,
            height: d.height || 0,
            source: d.source || 'bgRemover',
            createdAt: d.createdAt ? new Date(d.createdAt).getTime() : Date.now(),
            backend: true
          }));
          this.totalCount = res.data.totalDocs || backendList.length;
          this.totalPages = res.data.totalPages || 1;
          usedBackend = true;
          this.maybeMigrateLocal();
        }
      } catch (e) { /* fall back to local */ }
    }

    // 백엔드 실패시 또는 추가 표시용으로 localStorage 캐시 병합
    if (!usedBackend) {
      const localList = getMyImages().map(it => ({...it, backend: false}));
      this.images = this.applySort(this.applyClientFilter(localList));
      this.totalCount = this.images.length;
      this.totalPages = 1;
    } else {
      this.images = this.applySort(backendList);
    }
    this.isLoading = false;
  };

  // localStorage에 있고 백엔드에 없는 이미지를 1회 마이그레이션
  maybeMigrateLocal = async () => {
    const localList = getMyImages();
    if (!localList.length) return;
    const existsKey = 'orp_my_images_migrated';
    if (localStorage.getItem(existsKey) === '1') return;
    for (const it of localList) {
      try {
        await this.props.addMyImage({
          store_id: this.store._id,
          shopNo: this.store.shopNo || 1,
          name: it.name,
          url: it.url,
          size: it.size,
          width: it.width,
          height: it.height,
          source: it.source || 'bgRemover',
          tags: []
        });
      } catch (e) {}
    }
    try { localStorage.setItem(existsKey, '1'); } catch (e) {}
  };

  applyClientFilter = (list) => {
    let out = list;
    const kw = (this.keyword || '').trim().toLowerCase();
    if (kw) out = out.filter(img => (img.name || '').toLowerCase().includes(kw));
    if (this.sourceFilter) out = out.filter(img => (img.source || '') === this.sourceFilter);
    return out;
  };

  applySort = (list) => {
    const arr = [...list];
    switch (this.sortKey) {
      case 'date_asc':  return arr.sort((a, b) => a.createdAt - b.createdAt);
      case 'name_asc':  return arr.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      case 'size_desc': return arr.sort((a, b) => (b.size || 0) - (a.size || 0));
      case 'date_desc':
      default:          return arr.sort((a, b) => b.createdAt - a.createdAt);
    }
  };

  @action onKeywordChange = (e) => {
    this.keyword = e.target.value;
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => {
      this.page = 1;
      this.reload();
    }, 300);
  };

  @action onSourceChange = (e) => {
    this.sourceFilter = e.target.value;
    this.page = 1;
    this.reload();
  };

  @action onSortChange = (e) => {
    this.sortKey = e.target.value;
    this.images = this.applySort(this.images);
  };

  @action goPage = (p) => {
    if (p < 1 || p > this.totalPages) return;
    this.page = p;
    this.reload();
  };

  @action toggleSelectMode = () => {
    this.selectMode = !this.selectMode;
    if (!this.selectMode) this.selectedIds = [];
  };

  @action toggleSelect = (id) => {
    if (this.selectedIds.includes(id)) {
      this.selectedIds = this.selectedIds.filter(x => x !== id);
    } else {
      this.selectedIds = [...this.selectedIds, id];
    }
  };

  @action selectAll = () => {
    if (this.selectedIds.length === this.images.length) this.selectedIds = [];
    else this.selectedIds = this.images.map(i => i.id);
  };

  @action removeSelected = async () => {
    if (!this.selectedIds.length) return;
    if (!window.confirm(`선택한 ${this.selectedIds.length}개 이미지를 목록에서 삭제할까요? (카페24 원본은 유지됩니다)`)) return;
    const backendIds = this.selectedIds.filter(id => {
      const it = this.images.find(i => i.id === id);
      return it && it.backend;
    });
    const localIds = this.selectedIds.filter(id => !backendIds.includes(id));
    if (backendIds.length && this.hasStore) {
      try {
        await this.props.removeMyImages({ids: backendIds, store_id: this.store._id});
      } catch (e) { toast.error('삭제 중 일부 오류가 발생했습니다.'); }
    }
    localIds.forEach(id => removeLocal(id));
    this.selectedIds = [];
    this.selectMode = false;
    this.reload();
    toast.success('선택 항목이 삭제되었습니다.');
  };

  @action startRename = (img) => {
    this.editingId = img.id;
    this.editingName = img.name;
  };

  @action cancelRename = () => {
    this.editingId = null;
    this.editingName = '';
  };

  @action applyRename = async () => {
    if (!this.editingId) return;
    const name = (this.editingName || '').trim() || '이미지';
    const target = this.images.find(i => i.id === this.editingId);
    if (target && target.backend && this.hasStore) {
      try {
        await this.props.updateMyImage({id: this.editingId, store_id: this.store._id, name});
      } catch (e) { toast.error('이름 변경 실패'); }
    } else {
      renameLocal(this.editingId, name);
    }
    this.editingId = null;
    this.editingName = '';
    this.reload();
    toast.success('이름이 변경되었습니다.');
  };

  @action remove = async (img) => {
    if (!window.confirm(`"${img.name}" 을(를) 목록에서 삭제할까요? (카페24 원본은 유지됩니다)`)) return;
    if (img.backend && this.hasStore) {
      try {
        await this.props.removeMyImage({id: img.id, store_id: this.store._id});
      } catch (e) { toast.error('삭제 실패'); return; }
    } else {
      removeLocal(img.id);
    }
    this.reload();
  };

  copyUrl = (url) => {
    try {
      const tmp = document.createElement('textarea');
      tmp.value = url;
      document.body.appendChild(tmp);
      tmp.select();
      document.execCommand('copy');
      document.body.removeChild(tmp);
      toast.success('URL이 복사되었습니다.');
    } catch (e) {
      toast.error('URL 복사에 실패했습니다.');
    }
  };

  formatSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
  };

  @action onPickFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) this.uploadFiles(files);
    e.target.value = '';
  };

  @action uploadFiles = async (files) => {
    if (!this.hasStore) {
      toast.error('스토어 정보를 확인할 수 없습니다.');
      return;
    }
    const imgFiles = files.filter(f => f.type && f.type.startsWith('image/'));
    if (!imgFiles.length) {
      toast.error('이미지 파일만 업로드 가능합니다.');
      return;
    }
    this.isUploading = true;
    try {
      // 디스크 경유 없이 base64 → 카페24 직접 업로드 (안정성↑)
      const base64Imgs = [];
      const dims = [];
      for (const f of imgFiles) {
        const b64 = await blobToBase64(f);
        const d = await this.readImageSize(f);
        base64Imgs.push({image: b64});
        dims.push(d);
      }

      const cafe24Res = await this.props.base64UploadImages({
        store_id: this.store._id,
        base64Imgs
      });
      if (cafe24Res && cafe24Res.error) {
        throw new Error(cafe24Res.error.message || '카페24 업로드 실패');
      }
      if (!cafe24Res || !cafe24Res.images || !cafe24Res.images.length) {
        throw new Error('cafe24 upload failed');
      }

      // 백엔드에 메타 저장
      for (let i = 0; i < cafe24Res.images.length; i++) {
        const url = cafe24Res.images[i].path;
        const file = imgFiles[i] || imgFiles[0];
        const dim = dims[i] || {width: 0, height: 0};
        try {
          await this.props.addMyImage({
            store_id: this.store._id,
            shopNo: this.store.shopNo || 1,
            name: file.name || '이미지',
            url,
            size: file.size || 0,
            width: dim.width,
            height: dim.height,
            source: 'upload',
            tags: []
          });
        } catch (e) {}
      }
      toast.success(`${cafe24Res.images.length}개 이미지가 업로드되었습니다.`);
      this.page = 1;
      this.reload();
    } catch (e) {
      toast.error(e && e.message ? e.message : '업로드에 실패했습니다.');
    } finally {
      this.isUploading = false;
    }
  };

  readImageSize = (file) => new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { resolve({width: img.naturalWidth, height: img.naturalHeight}); URL.revokeObjectURL(url); };
    img.onerror = () => { resolve({width: 0, height: 0}); URL.revokeObjectURL(url); };
    img.src = url;
  });

  @action openLightbox = (img) => { this.lightboxImg = img; };
  @action closeLightbox = () => { this.lightboxImg = null; };

  renderSourceBadge = (src) => {
    const cfg = SOURCE_LABEL[src] || SOURCE_LABEL.imported;
    return (
      <span className="my-image-card__badge" style={{color: cfg.color, background: cfg.bg}}>
        {cfg.label}
      </span>
    );
  };

  render() {
    const list = this.images;
    const allSelected = list.length > 0 && this.selectedIds.length === list.length;
    return (
      <div className="main-content">
        <PageTitle/>
        <div className="widget-box my-images-page">

          <div className="mip-header">
            <div className="mip-header__title">
              <p>카페24에 업로드된 이미지를 한 곳에서 관리합니다. 배경제거 결과·직접 업로드 모두 자동 등록됩니다.</p>
            </div>
            <div className="mip-header__cta">
              <input type="file" multiple accept="image/*" ref={this.fileInputRef} style={{display: 'none'}} onChange={this.onPickFiles}/>
              <Button theme="primary" disabled={this.isUploading} onClick={() => this.fileInputRef.current && this.fileInputRef.current.click()}>
                <FeatherIcon icon={this.isUploading ? 'loader' : 'upload'} size="14"/>
                <span>{this.isUploading ? ' 업로드 중…' : ' 이미지 업로드'}</span>
              </Button>
            </div>
          </div>

          <div className="mip-toolbar">
            <div className="mip-toolbar__filters">
              <div className="mip-search">
                <FeatherIcon icon="search" size="14"/>
                <FormInput placeholder="이름으로 검색" value={this.keyword} onChange={this.onKeywordChange}/>
              </div>
              <FormSelect value={this.sourceFilter} onChange={this.onSourceChange} className="mip-select">
                <option value="">전체 소스</option>
                <option value="bgRemover">배경제거</option>
                <option value="upload">직접업로드</option>
                <option value="banner">배너</option>
                <option value="imported">가져옴</option>
              </FormSelect>
              <FormSelect value={this.sortKey} onChange={this.onSortChange} className="mip-select">
                <option value="date_desc">최신순</option>
                <option value="date_asc">오래된순</option>
                <option value="name_asc">이름순</option>
                <option value="size_desc">큰 용량순</option>
              </FormSelect>
            </div>

            <div className="mip-toolbar__meta">
              <div className="mip-count">
                전체 <b>{this.totalCount}</b>개
                {this.selectMode && this.selectedIds.length > 0 && <span className="mip-count__sel"> · 선택 {this.selectedIds.length}개</span>}
              </div>
            </div>

            <div className="mip-toolbar__actions">
              {this.selectMode && (
                <>
                  <Button theme="light" size="sm" onClick={this.selectAll}>
                    <FeatherIcon icon={allSelected ? 'square' : 'check-square'} size="14"/>
                    <span>{allSelected ? ' 전체해제' : ' 전체선택'}</span>
                  </Button>
                  <Button theme="danger" size="sm" disabled={!this.selectedIds.length} onClick={this.removeSelected}>
                    <FeatherIcon icon="trash-2" size="14"/>
                    <span> 선택삭제</span>
                  </Button>
                </>
              )}
              <Button theme={this.selectMode ? 'warning' : 'light'} size="sm" onClick={this.toggleSelectMode}>
                <FeatherIcon icon={this.selectMode ? 'x' : 'check-square'} size="14"/>
                <span>{this.selectMode ? ' 선택종료' : ' 선택'}</span>
              </Button>
              <Button theme="light" size="sm" onClick={() => this.reload()} title="새로고침">
                <FeatherIcon icon="refresh-cw" size="14"/>
              </Button>
            </div>
          </div>

          {this.isLoading ? (
            <div className="mip-state">
              <FeatherIcon icon="loader" size="36"/>
              <div className="mip-state__text">불러오는 중…</div>
            </div>
          ) : list.length === 0 ? (
            <div className="mip-state">
              <FeatherIcon icon="image" size="48"/>
              <div className="mip-state__text">{this.keyword || this.sourceFilter ? '조건에 맞는 이미지가 없습니다.' : '등록된 이미지가 없습니다.'}</div>
              {!(this.keyword || this.sourceFilter) && (
                <div className="mip-state__hint">
                  <code>이미지 배경제거</code> 결과 또는 우측 상단 <b>이미지 업로드</b> 버튼으로 추가하세요.
                </div>
              )}
              {!(this.keyword || this.sourceFilter) && (
                <Button theme="primary" size="sm" onClick={() => this.fileInputRef.current && this.fileInputRef.current.click()}>
                  <FeatherIcon icon="upload" size="14"/><span> 지금 업로드</span>
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="my-images-grid">
                {list.map((img) => {
                  const checked = this.selectedIds.includes(img.id);
                  return (
                    <div key={img.id} className={`my-image-card ${checked ? 'is-selected' : ''}`}>
                      <div className="my-image-card__thumb"
                           onClick={() => this.selectMode ? this.toggleSelect(img.id) : this.openLightbox(img)}>
                        <img src={img.url} alt={img.name}/>
                        {this.renderSourceBadge(img.source)}
                        {this.selectMode && (
                          <span className="my-image-card__check">
                            <FeatherIcon icon={checked ? 'check-square' : 'square'} size="20"/>
                          </span>
                        )}
                      </div>
                      <div className="my-image-card__body">
                        {this.editingId === img.id ? (
                          <div className="my-image-card__rename">
                            <FormInput value={this.editingName}
                                       onChange={(e) => this.editingName = e.target.value}
                                       onKeyDown={(e) => e.key === 'Enter' && this.applyRename()}/>
                            <Button theme="primary" size="sm" onClick={this.applyRename}>저장</Button>
                            <Button theme="light" size="sm" onClick={this.cancelRename}>취소</Button>
                          </div>
                        ) : (
                          <div className="my-image-card__name" title={img.name}>{img.name}</div>
                        )}
                        <div className="my-image-card__meta">
                          {img.width > 0 && <span>{img.width}×{img.height}</span>}
                          <span>{this.formatSize(img.size)}</span>
                          <span>{moment(img.createdAt).format('YYYY-MM-DD HH:mm')}</span>
                        </div>
                        <div className="my-image-card__url">
                          <input readOnly value={img.url} onClick={(e) => e.target.select()}/>
                        </div>
                        <div className="my-image-card__actions">
                          <Button theme="dark" size="sm" onClick={() => this.copyUrl(img.url)}>
                            <FeatherIcon icon="copy" size="14"/> URL
                          </Button>
                          {this.editingId !== img.id && (
                            <Button theme="light" size="sm" onClick={() => this.startRename(img)}>
                              <FeatherIcon icon="edit-2" size="14"/> 이름
                            </Button>
                          )}
                          <Button theme="danger" size="sm" onClick={() => this.remove(img)}>
                            <FeatherIcon icon="trash-2" size="14"/>
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {this.totalPages > 1 && (
                <div className="mip-pager">
                  <Button size="sm" theme="light" disabled={this.page <= 1} onClick={() => this.goPage(this.page - 1)}>
                    <FeatherIcon icon="chevron-left" size="14"/>
                  </Button>
                  <span>{this.page} / {this.totalPages}</span>
                  <Button size="sm" theme="light" disabled={this.page >= this.totalPages} onClick={() => this.goPage(this.page + 1)}>
                    <FeatherIcon icon="chevron-right" size="14"/>
                  </Button>
                </div>
              )}
            </>
          )}

          {this.lightboxImg && (
            <div className="my-images-lightbox" onClick={this.closeLightbox}>
              <div className="my-images-lightbox__inner" onClick={(e) => e.stopPropagation()}>
                <button className="my-images-lightbox__close" onClick={this.closeLightbox}>
                  <FeatherIcon icon="x" size="24"/>
                </button>
                <img src={this.lightboxImg.url} alt={this.lightboxImg.name}/>
                <div className="my-images-lightbox__info">
                  <div className="my-images-lightbox__name">{this.lightboxImg.name}</div>
                  <div className="my-images-lightbox__meta">
                    {this.lightboxImg.width > 0 && <span>{this.lightboxImg.width}×{this.lightboxImg.height}</span>}
                    <span>{this.formatSize(this.lightboxImg.size)}</span>
                    {this.renderSourceBadge(this.lightboxImg.source)}
                  </div>
                  <div className="my-images-lightbox__url">
                    <input readOnly value={this.lightboxImg.url} onClick={(e) => e.target.select()}/>
                    <Button theme="dark" size="sm" onClick={() => this.copyUrl(this.lightboxImg.url)}>
                      <FeatherIcon icon="copy" size="14"/> 복사
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    );
  }
}

export default MyImages;
